import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BackupRecoveryPanel = ({ metrics, onRefresh }) => {
  return (
    <div className="space-y-6">
      {/* Backup Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">Last Backup</p>
              <div className="flex items-center gap-2">
                <Icon name="Database" size={20} className="text-green-600" />
                <span className="text-lg font-bold text-green-900 dark:text-green-100">
                  {metrics?.lastBackup || 'Never'}
                </span>
              </div>
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">Automated daily backups</p>
            </div>
            <Icon name="CheckCircle" size={24} className="text-green-600" />
          </div>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Recovery Point Objective</p>
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={20} className="text-blue-600" />
                <span className="text-lg font-bold text-blue-900 dark:text-blue-100">24 hours</span>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">Maximum data loss window</p>
            </div>
            <Icon name="Info" size={24} className="text-blue-600" />
          </div>
        </div>
      </div>

      {/* Backup Configuration */}
      <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Icon name="Settings" size={20} />
          Backup Configuration
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600">
            <div className="flex items-center gap-3">
              <Icon name="RefreshCw" size={18} className="text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Automated Daily Backups</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Runs at 2:00 AM PNG time</p>
              </div>
            </div>
            <span className="px-3 py-1 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">
              Enabled
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600">
            <div className="flex items-center gap-3">
              <Icon name="Copy" size={18} className="text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Geo-Redundant Replication</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Data replicated to secondary region</p>
              </div>
            </div>
            <span className="px-3 py-1 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">
              Active
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600">
            <div className="flex items-center gap-3">
              <Icon name="Archive" size={18} className="text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Retention Policy</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">7 years (PNG labor law compliance)</p>
              </div>
            </div>
            <span className="px-3 py-1 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">
              Compliant
            </span>
          </div>
        </div>
      </div>

      {/* Disaster Recovery */}
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-6 border border-amber-200 dark:border-amber-800">
        <div className="flex items-start gap-3 mb-4">
          <Icon name="AlertTriangle" size={24} className="text-amber-600 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2">
              Disaster Recovery Options
            </h4>
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
              Automated failover to secondary region in case of primary region failure.
              Recovery Time Objective (RTO): 4 hours
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="default" size="sm" iconName="Play">
            Test Failover
          </Button>
          <Button variant="outline" size="sm" iconName="FileText">
            View DR Plan
          </Button>
        </div>
      </div>

      {/* Manual Backup Actions */}
      <div className="flex gap-3">
        <Button variant="default" size="sm" iconName="Database">
          Run Manual Backup Now
        </Button>
        <Button variant="outline" size="sm" iconName="RotateCcw">
          Restore from Backup
        </Button>
        <Button variant="outline" size="sm" iconName="Download">
          Download Backup Report
        </Button>
      </div>
    </div>
  );
};

export default BackupRecoveryPanel;